/**
 * Utilitaires pour gérer les types d'énergie des cartes Pokémon
 */

type EnergyType = string;

/**
 * Correspondance entre les types d'énergie en français et en anglais
 */
const FR_TO_EN: Record<string, string> = {
  'Feu': 'Fire',
  'Eau': 'Water',
  'Plante': 'Grass',
  'Électrique': 'Electric',
  'Psy': 'Psychic',
  'Combat': 'Fighting',
  'Obscurité': 'Dark',
  'Métal': 'Steel',
  'Dragon': 'Dragon',
  'Fée': 'Fairy',
  'Incolore': 'Normal',
  'Poison': 'Poison',
  'Sol': 'Ground',
  'Vol': 'Flying',
  'Insecte': 'Bug',
  'Roche': 'Rock',
  'Spectre': 'Ghost',
  'Glace': 'Ice',
  'Stellaire': 'Stellar',
};

/**
 * Correspondance entre les types d'énergie en anglais et en français
 */
const EN_TO_FR: Record<string, string> = {
  'Fire': 'Feu',
  'Water': 'Eau',
  'Grass': 'Plante',
  'Electric': 'Électrique',
  'Psychic': 'Psy',
  'Fighting': 'Combat',
  'Dark': 'Obscurité',
  'Steel': 'Métal',
  'Dragon': 'Dragon',
  'Fairy': 'Fée',
  'Normal': 'Incolore',
  'Poison': 'Poison',
  'Ground': 'Sol',
  'Flying': 'Vol',
  'Bug': 'Insecte',
  'Rock': 'Roche',
  'Ghost': 'Spectre',
  'Ice': 'Glace',
  'Stellar': 'Stellaire',
};

/**
 * Traduit un type d'énergie selon la langue cible
 * @param energy - Le type d'énergie à traduire
 * @param targetLang - La langue cible ('fr' ou 'en')
 * @returns Le type d'énergie traduit, ou l'original si pas de traduction disponible
 */
export const translateEnergyType = (energy: EnergyType | null, targetLang: 'fr' | 'en'): EnergyType | null => {
  if (!energy) return null;
  
  // Déterminer si le type est en français ou en anglais
  const isFrench = FR_TO_EN[energy] !== undefined;
  const isEnglish = EN_TO_FR[energy] !== undefined;
  
  // Si on ne peut pas déterminer la langue ou si c'est déjà la langue cible, renvoyer tel quel
  if ((!isFrench && !isEnglish) || (isFrench && targetLang === 'fr') || (isEnglish && targetLang === 'en')) {
    return energy;
  }
  
  // Traduire selon la direction nécessaire
  return targetLang === 'en' ? FR_TO_EN[energy] || energy : EN_TO_FR[energy] || energy;
};

/**
 * Obtient tous les types d'énergie disponibles dans une langue spécifique
 * @param lang - La langue pour laquelle récupérer les types d'énergie ('fr' ou 'en') 
 * @returns Un tableau des types d'énergie dans la langue spécifiée
 */
export const getEnergyTypesByLang = (lang: 'fr' | 'en'): EnergyType[] => {
  if (lang === 'fr') {
    return Object.keys(FR_TO_EN);
  } else {
    return Object.keys(EN_TO_FR);
  }
}; 